import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import FadeIn, { SectionHeading } from "./FadeIn";
import Magnetic from "./motion/Magnetic";

const CATEGORIES = [
  {
    id: "getting-started",
    label: "Getting Started",
    items: [
      {
        q: "How much do I need for a down payment in Canada?",
        a: "The minimum is 5% on the first $500,000 of the purchase price and 10% on the portion between $500,000 and $1.5 million. Homes above $1.5 million — or any property where you want to avoid mortgage default insurance — require at least 20% down. A larger down payment lowers your monthly payment and can improve your rate options.",
      },
      {
        q: "What is a mortgage pre-approval, and why should I get one?",
        a: "A pre-approval is a lender's written estimate of how much you can borrow, based on your income, credit and down payment — usually with a rate hold of 90 to 120 days. It tells you exactly what price range to shop in, strengthens your offers in competitive situations, and surfaces any credit or documentation issues early, while there's still time to fix them.",
      },
      {
        q: "What credit score do I need to qualify?",
        a: "Most lenders look for a score of 680 or higher for the best insured rates, though options exist below that. If your score needs work, don't self-reject — part of my job is reviewing your credit picture and mapping the fastest realistic path to an approval.",
      },
      {
        q: "I'm self-employed. Can I still get a mortgage?",
        a: "Yes. Self-employed buyers are approved every day — the documentation is simply different. Depending on your situation we can use two years of tax returns, business financials, or stated-income programs through alternative lenders. The key is packaging your file properly, which is where an agent earns their keep.",
      },
    ],
  },
  {
    id: "rates-options",
    label: "Rates & Options",
    items: [
      {
        q: "Should I choose a fixed or variable rate?",
        a: "Fixed gives you payment certainty for the term — ideal if budget stability matters most. Variable typically starts lower and lets you benefit if rates fall, but your payment or amortization can move with the prime rate. The right answer depends on your cash flow, risk tolerance and how long you plan to keep the property — we'll model both before you decide.",
      },
      {
        q: "What is mortgage default insurance (CMHC insurance)?",
        a: "If your down payment is under 20%, federal rules require default insurance from CMHC, Sagen or Canada Guaranty. The premium — 2.8% to 4% of the mortgage amount — is usually added to your mortgage rather than paid upfront. It protects the lender, and in exchange you get access to lower insured rates.",
      },
      {
        q: "What amortization period should I choose?",
        a: "A longer amortization (up to 30 years in many cases) means lower monthly payments but more interest over time; a shorter one builds equity faster. Many clients start longer for cash-flow flexibility and use prepayment privileges to accelerate when they can — flexibility first, discipline second.",
      },
      {
        q: "Can I pay my mortgage off faster without penalty?",
        a: "Almost always, yes. Most mortgages include prepayment privileges — typically 10% to 20% lump-sum payments per year plus the ability to increase regular payments. Choosing bi-weekly accelerated payments alone can cut years off your amortization. I'll make sure your mortgage has the privileges that match your plans.",
      },
    ],
  },
  {
    id: "process-costs",
    label: "Process & Costs",
    items: [
      {
        q: "What documents will I need to apply?",
        a: "Typically: government ID, recent pay stubs and an employment letter (or business documents if self-employed), two years of T4s or Notices of Assessment, 90 days of bank statements showing your down payment, and details of any existing debts. I'll give you a precise checklist for your situation so nothing gets missed.",
      },
      {
        q: "What closing costs should I budget for?",
        a: "Plan for roughly 1.5% to 4% of the purchase price on top of your down payment: legal fees, title insurance, land transfer tax (where applicable), appraisal, home inspection and property tax adjustments. First-time buyers may qualify for rebates. I'll walk you through an exact estimate for your purchase before you commit.",
      },
      {
        q: "How long does the mortgage process take?",
        a: "A pre-approval can often be completed within 24 to 48 hours once documents are in. A full approval on a specific property typically takes a few days to two weeks, depending on the lender, the appraisal and how complete the file is. My process is built around clean, complete submissions — that's what keeps timelines short.",
      },
      {
        q: "Why work with a mortgage agent instead of going to my bank?",
        a: "Your bank can offer you its own products. I compare dozens of lenders — major banks, credit unions and monoline lenders — to find the structure and rate that fit you, and my services are typically free to you on standard residential mortgages. You get one application, expert negotiation, and advice that's loyal to you rather than to a single institution.",
      },
    ],
  },
];

export default function Faq({ onLead }) {
  return (
    <section id="faq" data-testid="faq-section" className="relative py-24 lg:py-32 bg-charcoal-800/95">
      <div className="max-w-4xl mx-auto px-6 lg:px-10">
        <SectionHeading
          label="Common Questions"
          align="center"
          title={
            <>
              ANSWERS, <span className="gold-text-gradient italic">CLEARLY EXPLAINED.</span>
            </>
          }
          sub="The questions Canadian buyers and homeowners ask most — answered in plain language. For advice on your specific situation, book a consultation."
        />

        <div className="mt-16 space-y-14">
          {CATEGORIES.map((cat, ci) => (
            <FadeIn key={cat.id} delay={ci * 0.08}>
              <div data-testid={`faq-category-${cat.id}`}>
                <p className="gold-label mb-6">
                  <span className="text-ivory/35 mr-3">{String(ci + 1).padStart(2, "0")}</span>
                  {cat.label}
                </p>
                <Accordion type="single" collapsible className="glass-panel rounded-md px-7 lg:px-9">
                  {cat.items.map((item, ii) => {
                    const num = ci * 10 + ii;
                    return (
                      <AccordionItem key={ii} value={`item-${num}`} className="border-white/10 last:border-b-0">
                        <AccordionTrigger
                          data-testid={`faq-trigger-${num}`}
                          className="py-6 text-left font-serif text-lg lg:text-xl text-ivory/85 hover:text-gold hover:no-underline transition-colors duration-300 [&>svg]:text-gold/60"
                        >
                          {item.q}
                        </AccordionTrigger>
                        <AccordionContent
                          data-testid={`faq-content-${num}`}
                          className="text-sm lg:text-base text-ivory/60 font-light leading-relaxed pr-8 pb-6"
                        >
                          {item.a}
                        </AccordionContent>
                      </AccordionItem>
                    );
                  })}
                </Accordion>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.1} className="mt-16 text-center">
          <p className="text-sm text-ivory/55 font-light max-w-xl mx-auto leading-relaxed">
            These answers are general information, not financial advice — every file is different.
          </p>
          <Magnetic strength={0.25}>
            <button
              data-testid="faq-cta-btn"
              onClick={() => onLead("consultation")}
              className="btn-sweep mt-7 px-9 py-4 text-xs tracking-[0.2em] font-semibold text-charcoal bg-gold rounded-sm"
            >
              <span className="btn-sweep-text">ASK AKASHDEEP DIRECTLY</span>
            </button>
          </Magnetic>
        </FadeIn>
      </div>
    </section>
  );
}
